import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';

export default function Landing() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const { login, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  async function handleGoogleSignIn() {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
      navigate('/dashboard');
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError('Failed to sign in with Google');
        console.error('Google sign-in error:', err);
      }
    }
    setLoading(false);
  }

  async function handleEmailLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.code === 'auth/invalid-credential'
        ? 'Invalid email or password'
        : 'Failed to sign in');
    }
    setLoading(false);
  }

  return (
    <div className="landing-page">
      {/* Top Nav */}
      <nav className="landing-nav">
        <img src="/favicon.svg" alt="MileDeduct" className="landing-nav-logo" />
        <button className="landing-sign-in-btn" onClick={() => setShowSignIn(!showSignIn)}>
          Sign In
        </button>
      </nav>

      {/* Sign In Dropdown */}
      {showSignIn && (
        <div className="signin-dropdown">
          <div className="signin-dropdown-card">
            {error && <div className="error-message">{error}</div>}

            <button
              className="btn btn-google"
              onClick={handleGoogleSignIn}
              disabled={loading}
            >
              <svg className="google-icon" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {loading ? 'Signing in...' : 'Continue with Google'}
            </button>

            {!showEmailForm ? (
              <button className="btn btn-secondary" onClick={() => setShowEmailForm(true)}>
                Sign in with Email
              </button>
            ) : (
              <>
                <div className="auth-divider"><span>or</span></div>
                <form onSubmit={handleEmailLogin}>
                  <div className="form-group">
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email address" />
                  </div>
                  <div className="form-group">
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Password" />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>
                </form>
              </>
            )}
            <p className="auth-link" style={{ marginTop: '12px' }}>
              New here? <Link to="/signup">Create an account</Link>
            </p>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="landing-hero">
        <img src="/milededuct-logo.svg" alt="MileDeduct" className="landing-logo" />
        <h1>
          Just drive.<br />
          <span className="accent">We'll handle the deduction.</span>
        </h1>
        <p>
          Automatic mileage tracking for freelancers, gig workers, and small business owners.
          Every mile is money back.
        </p>

        {/* Pricing CTA — prominent */}
        <div className="landing-hero-pricing">
          <div className="hero-price">
            <span className="hero-price-amount">$39.99</span>
            <span className="hero-price-period">/year</span>
          </div>
          <div className="hero-price-note">Less than $0.77/week. One trip can pay for itself.</div>
          <button
            className="btn btn-primary hero-cta"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            Start 30-Day Free Trial
          </button>
        </div>
      </section>

      {/* Features */}
      <section className="landing-features">
        <div className="landing-features-title">Features</div>
        <h2>Everything you need to maximize deductions</h2>

        <div className="feature-grid">
          <div className="feature-card">
            <div className="feature-icon gps">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
            </div>
            <div>
              <h3>Auto-Drive Detection</h3>
              <p>MileDeduct detects when you start driving and records every mile automatically. No buttons to press.</p>
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon deduction">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
              </svg>
            </div>
            <div>
              <h3>IRS Deduction Calculator</h3>
              <p>Automatic deduction estimates using the current IRS standard mileage rate. See your savings in real time.</p>
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon vehicle">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
              </svg>
            </div>
            <div>
              <h3>Multi-Vehicle Support</h3>
              <p>Track up to 2 vehicles with separate odometer readings. Perfect for personal and business cars.</p>
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon report">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
              </svg>
            </div>
            <div>
              <h3>Trip History & Reports</h3>
              <p>Filter by vehicle, date range, or purpose. Export IRS-ready reports when tax season arrives.</p>
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon deduction">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.5 3.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5zM19 19.09H5V4.91h14v14.18zM6 15h12v2H6zm0-4h12v2H6zm0-4h12v2H6z"/>
              </svg>
            </div>
            <div>
              <h3>Expense Tracking</h3>
              <p>Scan receipts with your camera or upload files. OCR auto-extracts merchant, amount, and category.</p>
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon secure">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
              </svg>
            </div>
            <div>
              <h3>Secure & Private</h3>
              <p>Your data is encrypted and stored securely. We never sell your information. Your miles, your business.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-links">
          <Link to="/terms">Terms of Service</Link>
          <Link to="/privacy">Privacy Policy</Link>
        </div>
        <div className="copyright">
          &copy; {new Date().getFullYear()} MileDeduct. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
